import React, { useState } from 'react';
import { X } from 'lucide-react';
import { GoogleCalendarConfig } from '../tools/GoogleCalendarConfig';
import { CalcomTestButton } from '../tools/CalcomTestButton';
import { WebhookTestButton } from '../tools/WebhookTestButton';
import { SMSTestButton } from '../tools/SMSTestButton';

const triggerExamples = {
  email: ["Wenn der Anrufer nach einer E-Mail fragt", "Wenn ein Termin bestätigt wurde", "Wenn der Anrufer eine Zusammenfassung möchte"],
  sms: ["Wenn der Anrufer eine SMS-Bestätigung wünscht", "Wenn Termindetails per SMS gesendet werden sollen"],
  calendar: ["Wenn der Anrufer einen Termin vereinbaren möchte", "Wenn ein Besprechungstermin vereinbart wurde"],
  google_calendar: ["Wenn der Anrufer nach freien Terminen fragt", "Wenn Termine aus dem Kalender abgerufen werden sollen"],
  webhook: ["Wenn Anrufdaten ans CRM gesendet werden sollen", "Wenn ein Lead qualifiziert wurde"]
};

const instructionExamples = {
  email: [
    "Schreibe eine freundliche E-Mail mit allen im Gespräch genannten Preisen. Füge am Ende unsere Kontaktdaten hinzu.",
    "Erstelle eine Terminbestätigung mit Datum, Uhrzeit und Anfahrtsbeschreibung.",
    "Sende eine Zusammenfassung des Gesprächs mit den wichtigsten besprochenen Punkten."
  ],
  sms: [
    "Sende eine kurze Bestätigung mit Termin und Adresse.",
    "Bestätige die Anfrage mit einem Rückruf-Versprechen."
  ],
  calendar: [
    "Prüfe freie Termine für diese Woche und schlage dem Patienten 3 mögliche Termine vor.",
    "Lege einen Beratungstermin an mit Name, Telefonnummer und Anliegen des Patienten.",
    "Suche den nächsten freien Termin am Vormittag und bestätige ihn dem Patienten."
  ],
  google_calendar: [
    "Rufe alle Termine der nächsten 7 Tage ab und nenne freie Zeitfenster.",
    "Prüfe ob am gewünschten Tag noch Termine frei sind.",
    "Liste die nächsten 5 Termine mit Datum und Uhrzeit auf."
  ],
  webhook: [
    "Sende alle Kundendaten (Name, Telefon, E-Mail) und eine Zusammenfassung des Gesprächs ans CRM.",
    "Übermittle Lead-Informationen mit Interesse und Qualifizierungsstatus.",
    "Sende Terminbuchung mit allen Details an das Buchungssystem."
  ]
};

const toolIcons = { email: '📧', sms: '📱', calendar: '📅', google_calendar: '📆', webhook: '🔗' };

const instructionPlaceholders = {
  email: 'z.B. Sende dem Anrufer eine E-Mail mit allen besprochenen Preisen. Frage vorher nach der E-Mail-Adresse.',
  sms: 'z.B. Sende eine Terminbestätigung per SMS mit Datum, Uhrzeit und Adresse.',
  calendar: 'z.B. Wenn der Anrufer einen Termin möchte:\n1. Frage nach dem gewünschten Tag\n2. Prüfe freie Termine und nenne 3 Optionen\n3. Wenn er sich entschieden hat, frage nach Name und E-Mail\n4. Buche den Termin',
  webhook: 'z.B. Nach jedem Gespräch, sende Name und Anliegen des Anrufers an den Webhook.',
};

export function ToolConfigModal({ tool, onClose, onSave, onDelete, agentId }) {
  const [formData, setFormData] = useState({
    tool_type: tool.tool_type,
    name: tool.name || '',
    description: tool.description || '',
    trigger_condition: tool.trigger_condition || '',
    instructions: tool.instructions || '',
    enabled: tool.enabled !== false,
    config: tool.config || {}
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{toolIcons[formData.tool_type]}</span>
            <h2 className="text-lg font-semibold">{formData.name || formData.tool_type} konfigurieren</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Info */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
            <p className="font-medium text-gray-700">So werden diese Felder genutzt:</p>
            <p className="mt-1"><strong>Auslöser</strong> + <strong>Anweisungen</strong> werden automatisch an den System-Prompt angehängt. Der Agent folgt Ihren Anweisungen hier. Sie können alternativ alles im Haupt-Prompt definieren.</p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="z.B. Preisinfo Email, Terminbestätigung Email" />
          </div>

          {/* Trigger Condition */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <label className="block text-sm font-medium text-amber-800 mb-1">Auslöser — wann soll das Tool genutzt werden? (optional)</label>
            <p className="text-xs text-amber-600 mb-2">Wird in den Prompt eingefügt. Leer lassen = Agent entscheidet selbst.</p>
            <textarea value={formData.trigger_condition} onChange={(e) => setFormData({ ...formData, trigger_condition: e.target.value })} rows={2} className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="z.B. Wenn der Patient Preise per E-Mail haben möchte..." />
            {triggerExamples[formData.tool_type] && (
              <div className="mt-2 flex flex-wrap gap-2">
                {triggerExamples[formData.tool_type].map((ex, i) => (
                  <button key={i} type="button" onClick={() => setFormData({ ...formData, trigger_condition: ex })} className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors">{ex}</button>
                ))}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="block text-sm font-medium text-blue-800 mb-1">Anweisungen an den Agent</label>
            <p className="text-xs text-blue-600 mb-2">Diese Anweisungen werden direkt in den System-Prompt eingefügt. Definieren Sie hier genau, wie der Agent das Tool nutzen soll.</p>
            <textarea value={formData.instructions} onChange={(e) => setFormData({ ...formData, instructions: e.target.value })} rows={4} className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder={instructionPlaceholders[formData.tool_type] || 'Beschreibe was das Tool tun soll...'} data-testid="tool-instructions-input" />
            {instructionExamples[formData.tool_type] && (
              <div className="mt-2 flex flex-wrap gap-2">
                {instructionExamples[formData.tool_type].map((ex, i) => (
                  <button key={i} type="button" onClick={() => setFormData({ ...formData, instructions: ex })} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors">{ex.substring(0, 50)}...</button>
                ))}
              </div>
            )}
          </div>

          {/* Calendar tech info */}
          {formData.tool_type === 'calendar' && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
              <p className="font-medium text-gray-700 mb-1">Technische Info — Cal.com Pflichtfelder:</p>
              <ul className="ml-3 list-disc space-y-0.5">
                <li><strong>Nur Datum</strong> → Agent prüft verfügbare Zeiten</li>
                <li><strong>Datum + Uhrzeit + Name + E-Mail</strong> → Agent bucht den Termin</li>
              </ul>
              <p className="mt-1.5 text-gray-500">Tipp: Definieren Sie in den Anweisungen oben, in welcher Reihenfolge der Agent diese Infos abfragen soll.</p>
            </div>
          )}

          {/* Email Config */}
          {formData.tool_type === 'email' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700">SMTP Einstellungen</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Host</label>
                  <input type="text" placeholder="smtp.example.com" value={formData.config?.smtp_host || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, smtp_host: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Port</label>
                  <input type="number" placeholder="465" value={formData.config?.smtp_port || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, smtp_port: parseInt(e.target.value) }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Benutzer</label>
                  <input type="email" placeholder="user@example.com" value={formData.config?.smtp_user || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, smtp_user: e.target.value, from_email: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Passwort</label>
                  <input type="password" placeholder="••••••••" value={formData.config?.smtp_pass || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, smtp_pass: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            </div>
          )}

          {/* SMS Config */}
          {formData.tool_type === 'sms' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700">Twilio Einstellungen</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Account SID</label>
                  <input type="text" placeholder="AC..." value={formData.config?.account_sid || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, account_sid: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="sms-account-sid-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Auth Token</label>
                  <input type="password" placeholder="••••••••" value={formData.config?.auth_token || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, auth_token: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="sms-auth-token-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Absender-Nummer</label>
                  <input type="text" placeholder="+49..." value={formData.config?.from_number || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, from_number: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="sms-from-number-input" />
                </div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                <p className="font-medium">Twilio Zugangsdaten</p>
                <p className="mt-1">Account SID und Auth Token finden Sie in Ihrem <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">Twilio Dashboard</a>.</p>
                <p className="mt-0.5">Die Absender-Nummer muss eine verifizierte Twilio-Nummer sein.</p>
              </div>
              {formData.config?.account_sid && formData.config?.auth_token && formData.config?.from_number && (
                <SMSTestButton config={formData.config} />
              )}
            </div>
          )}

          {/* Webhook Config */}
          {formData.tool_type === 'webhook' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700">Webhook Einstellungen</h4>
              <div>
                <label className="block text-xs text-gray-500 mb-1">URL *</label>
                <input type="url" placeholder="https://n8n.example.com/webhook/..." value={formData.config?.url || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, url: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="webhook-url-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Methode</label>
                  <select value={formData.config?.method || 'POST'} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, method: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="webhook-method-select">
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Authentifizierung</label>
                  <select value={formData.config?.auth_type || 'none'} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, auth_type: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="webhook-auth-select">
                    <option value="none">Keine</option>
                    <option value="bearer">Bearer Token</option>
                    <option value="basic">Basic Auth</option>
                  </select>
                </div>
              </div>
              {formData.config?.auth_type && formData.config.auth_type !== 'none' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{formData.config.auth_type === 'bearer' ? 'Bearer Token' : 'Benutzername:Passwort'}</label>
                  <input type="password" placeholder={formData.config.auth_type === 'bearer' ? 'token...' : 'user:password'} value={formData.config?.auth_value || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, auth_value: e.target.value }})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="webhook-auth-value" />
                </div>
              )}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                <p className="font-medium">n8n / Make / Zapier Kompatibel</p>
                <p className="mt-1">Der Webhook sendet JSON mit: <code className="bg-blue-100 px-1 rounded">event</code>, <code className="bg-blue-100 px-1 rounded">timestamp</code>, <code className="bg-blue-100 px-1 rounded">data</code></p>
                <p className="mt-0.5">Headers: <code className="bg-blue-100 px-1 rounded">X-Webhook-Source: Wisecall</code>, <code className="bg-blue-100 px-1 rounded">X-Event-Type</code></p>
              </div>
              {formData.config?.url && <WebhookTestButton config={formData.config} />}
            </div>
          )}

          {/* Calendar Config */}
          {formData.tool_type === 'calendar' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700">Cal.com Einstellungen</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Anbieter</label>
                  <select value={formData.config.provider || 'calcom'} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, provider: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="calcom">Cal.com</option>
                    <option value="google">Google Calendar (bald)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Cal.com API Key *</label>
                  <input type="password" value={formData.config.api_key || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, api_key: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="cal_live_..." />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Username *</label>
                  <input type="text" value={formData.config.username || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, username: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="dein-cal-username" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Event Type Slug</label>
                  <input type="text" value={formData.config.event_type_slug || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, event_type_slug: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="beratung" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Event Type ID (optional)</label>
                  <input type="text" value={formData.config.event_type_id || ''} onChange={(e) => setFormData({ ...formData, config: { ...formData.config, event_type_id: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="123456" />
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg text-sm text-blue-800">
                <strong>So funktioniert's:</strong>
                <ul className="mt-1 ml-4 list-disc text-xs">
                  <li>API Key findest du unter cal.com/settings/developer/api-keys</li>
                  <li>Event Type Slug ist der URL-Teil deines Termins (z.B. "beratung" bei cal.com/user/beratung)</li>
                  <li>Event Type ID findest du in der URL wenn du den Event Type bearbeitest</li>
                </ul>
              </div>
              {agentId && formData.config?.api_key && <CalcomTestButton agentId={agentId} />}
            </div>
          )}

          {/* Google Calendar Config */}
          {formData.tool_type === 'google_calendar' && (
            <GoogleCalendarConfig config={formData.config} onChange={(newConfig) => setFormData({ ...formData, config: newConfig })} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          {tool.index >= 0 ? (
            <button onClick={onDelete} className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg">Löschen</button>
          ) : <div></div>}
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Abbrechen</button>
            <button onClick={() => onSave(formData)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}
